/**
 * The person's accounts (git hosting, package registries, tools), kept by provider-token-service in OpenBao and reached through the sandbox
 * gateway at `<vfsServiceUrl>/api/v1/me/accounts` (flow-helm-chart docs/git-accounts.md). Every answer is metadata:
 * no token ever comes back. A token is sent once, in the request that stores it, and never kept here.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

export class AccountsError extends Error {
  constructor(message, status, code = null, connectUrl = null) {
    super(message);
    this.name = 'AccountsError';
    this.status = status;
    this.code = code;
    this.connectUrl = connectUrl;
  }
}

const base = () => `${String(config.vfsServiceUrl || '').replace(/\/$/, '')}/api/v1/me/accounts`;

async function request(path, { method = 'GET', body } = {}) {
  if (!config.vfsServiceUrl) { throw new AccountsError('Accounts are not enabled on this Flow.', 404, 'accounts_off'); }
  let response;
  try {
    response = await getAuthorizedFetch()(`${base()}${path}`, {
      method,
      headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (error) {
    throw new AccountsError(`Your accounts could not be reached (${error.message}).`, 0, 'store_unreachable');
  }
  let parsed = null;
  try { parsed = await response.json(); } catch { parsed = null; }
  if (!response.ok) {
    throw new AccountsError(parsed?.error || parsed?.detail || `Request failed (${response.status})`, response.status, parsed?.code || null, parsed?.connectUrl || null);
  }
  return parsed;
}

const id = (value) => encodeURIComponent(String(value));

/**
 * `{ store, catalog, providers, accounts, connectUrl }`: `catalog` is the types Flow offers (absent on old servers),
 * `providers` the OAuth/token offers (admin apps, config providers) this person may use, disabled ones included.
 */
export const listAccounts = () => request('');

/**
 * A token (and a username where the type needs one), validated with the provider or registry before it is stored:
 * `{ account, test }`. `appId`: the admin app whose base URL and options it uses.
 */
export const addTokenAccount = ({ type, baseUrl, token, name, username, options, appId }) => request('', {
  method: 'POST',
  body: {
    type,
    token,
    ...(baseUrl ? { baseUrl } : {}),
    ...(name ? { name } : {}),
    ...(username ? { username } : {}),
    ...(options && Object.keys(options).length ? { options } : {}),
    ...(appId ? { appId } : {}),
  },
});

/** A new token for the same account (same login): `{ account, test }`. */
export const replaceToken = (accountId, token) => request(`/${id(accountId)}/token`, { method: 'PUT', body: { token } });

/** `{ authorizationUrl }` to send the browser to. `appId` (preferred) or `host` picks the OAuth app. */
export const startOauth = ({ type, appId, host, name, returnUri, replaceId }) => request('/oauth/start', {
  method: 'POST',
  body: {
    type,
    returnUri,
    ...(appId ? { appId } : {}),
    ...(host ? { host } : {}),
    ...(name ? { name } : {}),
    ...(replaceId ? { replaceId } : {}),
  },
});

export const testAccount = (accountId) => request(`/${id(accountId)}/test`, { method: 'POST' });

export const makeDefault = (accountId) => request(`/${id(accountId)}`, { method: 'PATCH', body: { isDefault: true } });

export const renameAccount = (accountId, name) => request(`/${id(accountId)}`, { method: 'PATCH', body: { name } });

/** `{ deleted, revoked, revokeNote }`. */
export const disconnectAccount = (accountId) => request(`/${id(accountId)}`, { method: 'DELETE' });
